import logger from "~/helpers/logger";
import { deriveScopedId } from "~/helpers/wsId";

export type IdempotencyField = "idempotencyKey" | "idempotentKey";

export interface InputFieldStrategy {
	field: IdempotencyField;
	scopePath?: string;
}

export interface NaturalKeyStrategy {
	naturalKeyPath: string;
}

export interface UnprotectedStrategy {
	unprotectedReason: string;
}

export type IdempotencyStrategy = InputFieldStrategy | NaturalKeyStrategy | UnprotectedStrategy;

export const isInputFieldStrategy = (strategy: IdempotencyStrategy): strategy is InputFieldStrategy =>
	"field" in strategy;

export const isNaturalKeyStrategy = (strategy: IdempotencyStrategy): strategy is NaturalKeyStrategy =>
	"naturalKeyPath" in strategy;

type GraphQLMutationFieldName = string;

export const IDEMPOTENCY_REGISTRY: Record<GraphQLMutationFieldName, IdempotencyStrategy> = {
	adjustInventoryItem: { field: "idempotencyKey" },
	atomicMove: { field: "idempotentKey" },
	attachAvailabilitySessionPhoto: { field: "idempotencyKey" },
	completeAssignmentTask: { field: "idempotentKey" },
	countMaterialItem: { field: "idempotencyKey" },
	createMovementOrder: { field: "idempotentKey" },
	discardFloatingTimeEntry: { naturalKeyPath: "floatingTimeEntryId" },
	gradeInventoryItem: { field: "idempotencyKey" },
	incrementInventoryItem: { field: "idempotencyKey" },
	linkFloatingTimeEntry: { naturalKeyPath: "floatingTimeEntryId" },
	moveInventoryItem: { field: "idempotencyKey" },
	moveMaterialItem: { field: "idempotencyKey" },
	postCycleCounts: { field: "idempotencyKey" },
	receiveMovementOrder: { field: "idempotentKey" },
	receiveVendorPurchaseOrderItems: { field: "idempotencyKey" },
	recordManualJobCost: { naturalKeyPath: "recordId" },
	recordShrinkEvent: { naturalKeyPath: "input.shrinkEventId" },
	removeShrinkEvent: { naturalKeyPath: "shrinkEventId" },
	scrapInventoryItem: { field: "idempotencyKey" },
	shipMovementOrder: { field: "idempotentKey" },
	skipAssignmentTask: { field: "idempotentKey" },
	startAssignmentTask: { field: "idempotentKey" },
	startFloatingTimer: { naturalKeyPath: "floatingTimeEntryId" },
	stopFloatingTimer: { naturalKeyPath: "floatingTimeEntryId" },
	submitOrderVerification: { field: "idempotencyKey", scopePath: "orderId" },
	submitVerifyContainer: { naturalKeyPath: "containerId" },
	// wsapi upserts each row on (tenant, serialId, locationId, recordedAt day) with $set, so a replay adds nothing.
	upsertRfidScans: { naturalKeyPath: "input.0.serialId" }
};

export const readPath = (input: unknown, path: string): unknown => {
	let current: unknown = input;
	for (const segment of path.split(".")) {
		if (!current || typeof current !== "object") return undefined;
		current = (current as Record<string, unknown>)[segment];
	}
	return current;
};

const readRequiredString = (input: unknown, path: string): string | undefined => {
	const value = readPath(input, path);
	return typeof value === "string" && value.length > 0 ? value : undefined;
};

export const deriveIdempotencyKey = (actionKey: string, scope?: string): string => {
	if (!actionKey) throw new Error("deriveIdempotencyKey requires a non-empty actionKey");
	if (scope === undefined) return actionKey;
	if (scope.length === 0) throw new Error("deriveIdempotencyKey requires a non-empty scope when one is supplied");
	return deriveScopedId(actionKey, scope);
};

const reportUnregistered = (mutationName: string): void => {
	if (import.meta.env.DEV) {
		throw new Error(
			`[offline] ${mutationName} is not in IDEMPOTENCY_REGISTRY — it would queue unprotected against replay`
		);
	}
	logger.warn(`[offline] ${mutationName} is not in IDEMPOTENCY_REGISTRY — it would queue unprotected against replay`);
};

export interface ApplyIdempotencyKeyArgs<TInput> {
	actionKey: string;
	input: TInput;
	mutationName: string;
}

const applyIdempotencyKey = <TInput extends Record<string, unknown>>({
	actionKey,
	input,
	mutationName
}: ApplyIdempotencyKeyArgs<TInput>): TInput => {
	if (!actionKey) throw new Error(`applyIdempotencyKey requires a non-empty actionKey for ${mutationName}`);

	const strategy = IDEMPOTENCY_REGISTRY[mutationName];
	if (!strategy) {
		reportUnregistered(mutationName);
		return input;
	}

	if (isNaturalKeyStrategy(strategy)) {
		if (!readRequiredString(input, strategy.naturalKeyPath)) {
			throw new Error(
				`${mutationName} is deduplicated by its natural key ${strategy.naturalKeyPath}, which is missing from the input`
			);
		}
		return input;
	}

	if (!isInputFieldStrategy(strategy)) {
		logger.warn(`[offline] ${mutationName} queues without replay protection: ${strategy.unprotectedReason}`);
		return input;
	}

	if (strategy.scopePath === undefined) {
		return { ...input, [strategy.field]: deriveIdempotencyKey(actionKey) };
	}

	const scope = readRequiredString(input, strategy.scopePath);
	if (!scope) {
		throw new Error(
			`${mutationName} mints one key per ${strategy.scopePath}, which is missing from the input — a shared key would let the server answer one aggregate with another's result`
		);
	}
	return { ...input, [strategy.field]: deriveIdempotencyKey(actionKey, scope) };
};

export default applyIdempotencyKey;
